/**
 * The redesign's described section header (§40.2) and hairline list (§40.3).
 *
 * A section header is its title 17 / 600 with, on the same baseline, a
 * description 13 #6E6E73 (measure 680, wrapping under the title when the line
 * is short) and at most **one** link at the right. It replaces the grey
 * paragraphs that used to sit between two sections.
 *
 * A hairline list is for a short list that is not a table — nothing to compare
 * across columns: a hairline above the first row and under each, rows at least
 * 56 high, an optional mark at the left (a workspace dot, or a 6 ring for a
 * thing to do oneself that Airlock cannot tick), the title 13.5 / 500 over its
 * description 13, and **one** thing at the right: an action link or a meta.
 * A "nothing to report" row keeps the same form, without colour.
 */
import type React from 'react';
import { color } from '../theme/tokens';

export function SectionHead({
  title,
  description,
  link,
  id
}: {
  title: string;
  description?: React.ReactNode;
  /** One link at most (§40.2). */
  link?: React.ReactNode;
  id?: string;
}): React.JSX.Element {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px 14px', flexWrap: 'wrap', marginBottom: 12 }}>
      <h2 id={id} style={{ margin: 0, fontSize: 17, fontWeight: 600, color: color.ink }}>
        {title}
      </h2>
      {description && (
        <p style={{ margin: 0, flex: '1 1 260px', maxWidth: 680, fontSize: 13, lineHeight: 1.5, color: color.inkSubtle }}>
          {description}
        </p>
      )}
      {link && <span style={{ marginLeft: 'auto', flex: 'none' }}>{link}</span>}
    </div>
  );
}

export function HairList({ children, label }: { children: React.ReactNode; label?: string }): React.JSX.Element {
  return (
    <ul aria-label={label} style={{ listStyle: 'none', margin: 0, padding: 0, borderTop: `1px solid ${color.hairSoft}` }}>
      {children}
    </ul>
  );
}

/** The mark at a row's left: a workspace dot of its colour, or the ring of a thing to do oneself. */
export type HairMark = { dot: string } | 'ring';

export function HairRow({
  title,
  description,
  mark,
  end
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  mark?: HairMark;
  /** One thing: an action link or a meta (13 #515154). */
  end?: React.ReactNode;
}): React.JSX.Element {
  return (
    <li
      className="al-hairrow"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        minHeight: 56,
        boxSizing: 'border-box',
        padding: '10px 0',
        borderBottom: `1px solid ${color.hairSoft}`
      }}
    >
      {mark &&
        (mark === 'ring' ? (
          <span
            aria-hidden
            style={{ flex: 'none', width: 6, height: 6, borderRadius: '50%', boxShadow: `inset 0 0 0 1px ${color.lineInput}` }}
          />
        ) : (
          <span aria-hidden style={{ flex: 'none', width: 10, height: 10, borderRadius: '50%', background: mark.dot }} />
        ))}
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 13.5, fontWeight: 500, color: color.ink }}>{title}</span>
        {description && (
          <span style={{ display: 'block', maxWidth: 680, fontSize: 13, lineHeight: 1.45, color: color.inkSubtle, marginTop: 2 }}>
            {description}
          </span>
        )}
      </span>
      {end && <span style={{ flex: 'none', fontSize: 13, color: color.inkMuted }}>{end}</span>}
    </li>
  );
}
